// src/hr/portal/AddPersonDialog.js -- "Add person" (spec §8.4). First name, last name and work email are
// required; the rest of the record is optional. Extra sign-in addresses are chips. The approver follows
// the manager until HR picks someone else. The onboarding checklist starts unless HR unticks it.
import React, { useMemo, useState } from 'react';
import { FiAlertCircle, FiUserPlus } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Drawer, Field } from '../kit';
import EmailChips, { withDraft } from './EmailChips';
import { EMAIL_RE, EMPLOYMENT_TYPES, fullName, isCurrent } from './portalUtils';

const NO_APPROVER = 'No approver — requests go straight to HR';

function AddPersonForm({ people, onClose, onDone }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', title: '', department: '', hireDate: '', employmentType: 'full-time', managerId: ''
  });
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState('');
  const [approverId, setApproverId] = useState(null); // null = follow the manager
  const [startOnboarding, setStartOnboarding] = useState(true);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const choices = useMemo(() => (people || []).filter(isCurrent)
    .map((p) => ({ value: p.id, label: fullName(p) }))
    .sort((a, b) => a.label.localeCompare(b.label)), [people]);
  // The server makes a new person's manager their approver when none is given, so "no approver"
  // is only offered while there is no manager (it can be cleared later on the profile).
  const approver = approverId === null || (approverId === '' && form.managerId) ? form.managerId : approverId;
  const approverOptions = form.managerId ? choices : [{ value: '', label: NO_APPROVER }, ...choices];
  const set = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const submit = async () => {
    if (busy) return;
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    const found = {};
    if (!firstName) found.firstName = 'Enter a first name';
    if (!lastName) found.lastName = 'Enter a last name';
    if (!EMAIL_RE.test(email)) found.email = 'Enter a valid work email';
    const extra = withDraft(emails, draft, email);
    if (extra.error) found.emails = extra.error;
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = { firstName, lastName, email, employmentType: form.employmentType, startOnboarding };
    if (extra.emails.length) body.emails = extra.emails;
    if (form.title.trim()) body.title = form.title.trim();
    if (form.department.trim()) body.department = form.department.trim();
    if (form.hireDate) body.hireDate = form.hireDate;
    if (form.managerId) body.managerId = form.managerId;
    if (approver) body.approverId = approver;

    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.people.create(body);
      onDone((result && result.person) || null, `${firstName} ${lastName}`);
    } catch (err) {
      setError(errorText(err, "Couldn't add this person. Try again."));
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Add person"
      subtitle="They don't need to have signed in yet. You can set up their balances right away."
      width={460}
      onSubmit={submit}
      className="hrp-addp"
      footer={(
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" icon={FiUserPlus} loading={busy}>Add person</Btn>
        </>
      )}
    >
      <div className="hrp-two">
        <Field label="First name" value={form.firstName} onChange={set('firstName')} error={errors.firstName} autoComplete="off" maxLength={80} required />
        <Field label="Last name" value={form.lastName} onChange={set('lastName')} error={errors.lastName} autoComplete="off" maxLength={80} required />
      </div>
      <Field
        label="Work email"
        type="email"
        value={form.email}
        onChange={set('email')}
        error={errors.email}
        hint="Their SaaS sign-in address links them to Time Off."
        autoComplete="off"
        required
      />
      <EmailChips
        emails={emails}
        onChange={(next) => { setEmails(next); if (errors.emails) setErrors((x) => ({ ...x, emails: undefined })); }}
        draft={draft}
        onDraftChange={setDraft}
        primary={form.email}
        error={errors.emails}
        hint="Any other address they sign in with, e.g. on @solu-m.com."
      />

      <div className="hrp-addp__sec">Record <span>(optional)</span></div>
      <div className="hrp-two">
        <Field label="Title" value={form.title} onChange={set('title')} maxLength={100} />
        <Field label="Department" value={form.department} onChange={set('department')} maxLength={100} />
      </div>
      <div className="hrp-two">
        <Field label="Hire date" type="date" value={form.hireDate} onChange={set('hireDate')} />
        <Field label="Employment type" type="select" value={form.employmentType} onChange={set('employmentType')} options={EMPLOYMENT_TYPES} />
      </div>
      <Field
        label="Manager"
        type="select"
        value={form.managerId}
        onChange={set('managerId')}
        options={[{ value: '', label: 'No manager' }, ...choices]}
      />
      <Field
        label="Approver (step 1)"
        type="select"
        value={approver}
        onChange={(e) => setApproverId(e.target.value)}
        options={approverOptions}
        hint="Follows the manager unless you pick someone else. HR always gives the final sign-off."
      />

      <label className="hrp-check">
        <input type="checkbox" checked={startOnboarding} onChange={(e) => setStartOnboarding(e.target.checked)} />
        <span>
          <b>Start the onboarding checklist</b>
          <em>Account approved, badge, laptop, policies, balances.</em>
        </span>
      </label>

      {error && (
        <div className="hrp-form-err" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </Drawer>
  );
}

export function AddPersonDialog({ open, ...props }) {
  if (!open) return null;
  return <AddPersonForm {...props} />;
}

export default AddPersonDialog;
